import React, { useEffect, useState } from "react";
import DashboardLayout from "../../layouts/DashboardLayout";
import { AdminService } from "../../../application/services/AdminService";
import {
  Building2,
  Users,
  TrendingUp,
  AlertCircle,
  FileText,
  Clock,
} from "lucide-react";
import { PagoService } from "../../../application/services/PagoService";
import { useAuth } from "../../../context/AuthContext";

const DashboardHome = () => {
  // Hooks
  const { user } = useAuth(); // Assuming useAuth provides 'user'

  const [stats, setStats] = useState({
    totalEmpresas: 0,
    activas: 0,
    suspendidas: 0,
    // Stats para Company Admin
    clientes: 0,
    medidores: 0,
    consumos: 0,
    pendientes: 0,
    recaudacionHoy: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    const fetchStats = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      setRefreshing(true);
      try {
        const isSuperAdmin =
          user?.isSuperAdmin ||
          user?.esSuperAdmin ||
          user?.roles?.includes("Super Admin") ||
          user?.roles?.includes("R");

        if (isSuperAdmin) {
          // Cargar stats globales de empresas
          const empresas = await AdminService.getEmpresas();
          const activas = empresas.filter((e) => e.estado === "activa").length;

          setStats((prev) => ({
            ...prev,
            totalEmpresas: empresas.length,
            activas,
            suspendidas: empresas.length - activas,
          }));
        } else {
          // Cargar stats específicos de la empresa para el periodo seleccionado
          const year = selectedDate.getFullYear();
          const month = selectedDate.getMonth();

          // Periodo actual: desde el 1 del mes seleccionado hasta el último día
          const startDate = new Date(year, month, 1)
            .toISOString()
            .split("T")[0];
          const endDate = new Date(year, month + 1, 0, 23, 59, 59)
            .toISOString()
            .split("T")[0];

          // Periodo anterior: para las lecturas
          const prevMonthStart = new Date(year, month - 1, 1)
            .toISOString()
            .split("T")[0];
          const prevMonthEnd = new Date(year, month, 0, 23, 59, 59)
            .toISOString()
            .split("T")[0];

          const [clientes, medidores, resumenActual, resumenAnterior] =
            await Promise.all([
              AdminService.getAllClientes(false, 1, 1).catch(() => ({
                total: 0,
              })),
              AdminService.getAllMedidores(1, 1).catch(() => ({ total: 0 })),
              PagoService.getResumenMensual(startDate, endDate).catch(() => ({
                recaudado: 0,
                pendientes_count: 0,
                emisiones_count: 0,
              })),
              PagoService.getResumenMensual(prevMonthStart, prevMonthEnd).catch(
                () => ({
                  recaudado: 0,
                  pendientes_count: 0,
                  emisiones_count: 0,
                }),
              ),
            ]);

          setStats((prev) => ({
            ...prev,
            clientes: clientes.total || clientes.count || 0,
            medidores: medidores.total || medidores.count || 0,
            consumos: resumenAnterior.emisiones_count || 0,
            pendientes: resumenAnterior.pendientes_count || 0,
            recaudacionHoy: resumenActual.recaudado || 0,
          }));
        }
      } catch (error) {
        console.error("Error cargando dashboard:", error);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    };

    if (user) {
      fetchStats();
    }
  }, [user, selectedDate]);

  const handleMonthChange = (e) => {
    const [y, m] = e.target.value.split("-");
    setSelectedDate(new Date(y, parseInt(m) - 1, 1));
  };

  const monthValue = `${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1).toString().padStart(2, "0")}`;

  const StatCard = ({
    title,
    value,
    icon: Icon,
    color,
    subtext,
    isCurrency,
  }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col items-start hover:shadow-md transition-shadow">
      <div className={`p-3 rounded-lg ${color} bg-opacity-10 text-opacity-100 mb-4`}>
        <Icon className={`w-8 h-8 ${color.replace("bg-", "text-")}`} />
      </div>
      <div className="w-full">
        <p className="text-sm font-medium text-gray-500 line-clamp-1">{title}</p>
        <h3 className="text-3xl font-bold text-gray-900 mt-1 truncate">
          {isCurrency ? `$${parseFloat(value).toFixed(2)}` : value}
        </h3>
        {subtext && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{subtext}</p>}
      </div>
    </div>
  );

  const isSuperAdmin = user?.isSuperAdmin || user?.esSuperAdmin;

  if (loading && !stats.clientes && !stats.totalEmpresas)
    return (
      <DashboardLayout title="Cargando...">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </DashboardLayout>
    );

  return (
    <DashboardLayout title="Dashboard General">
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            Bienvenido,{" "}
            {isSuperAdmin
              ? "Super Admin"
              : user?.nombre || user?.usuario || "Administrador"}
            {refreshing && (
              <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            )}
          </h1>
          <p className="text-gray-500">
            {isSuperAdmin
              ? "Resumen global del sistema SaaS."
              : "Resumen de gestión de tu Junta de Agua."}
          </p>
        </div>

        {!isSuperAdmin && (
          <div className="flex items-center gap-3 bg-white p-2 rounded-lg border border-gray-200 shadow-sm">
            <div className="p-2 bg-indigo-50 rounded-lg">
              <Clock className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                Periodo de Gestión
              </span>
              <input
                type="month"
                value={monthValue}
                onChange={handleMonthChange}
                className="text-sm font-bold text-gray-700 border-none p-0 focus:ring-0 cursor-pointer"
              />
            </div>
          </div>
        )}
      </div>

      <div
        className={`grid grid-cols-1 md:grid-cols-2 ${isSuperAdmin ? "lg:grid-cols-3" : "lg:grid-cols-5"} gap-4 mb-8`}
      >
        {isSuperAdmin ? (
          <>
            <StatCard
              title="Juntas Registradas"
              value={stats.totalEmpresas}
              icon={Building2}
              color="bg-blue-600"
              subtext="Total histórico"
            />
            <StatCard
              title="Juntas Activas"
              value={stats.activas}
              icon={TrendingUp}
              color="bg-green-600"
              subtext="Operando normalmente"
            />
            <StatCard
              title="Juntas Suspendidas"
              value={stats.suspendidas}
              icon={AlertCircle}
              color="bg-red-600"
              subtext="Requieren atención"
            />
          </>
        ) : (
          <>
            <StatCard
              title={`Lecturas ${new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1).toLocaleString("es-ES", { month: "long" }).charAt(0).toUpperCase() + new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1).toLocaleString("es-ES", { month: "long" }).slice(1)}`}
              value={stats.consumos}
              icon={FileText}
              color="bg-cyan-600"
              subtext="Ingresadas mes anterior"
            />
            <StatCard
              title="Por Cobrar"
              value={stats.pendientes}
              icon={Clock}
              color="bg-amber-600"
              subtext={`Emisiones ${new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1).toLocaleString("es-ES", { month: "long" })}`}
            />
            <StatCard
              title={`Recaudación ${selectedDate.toLocaleString("es-ES", { month: "long" }).charAt(0).toUpperCase() + selectedDate.toLocaleString("es-ES", { month: "long" }).slice(1)}`}
              value={stats.recaudacionHoy}
              icon={TrendingUp}
              color="bg-green-600"
              subtext="Total cobrado en el mes"
              isCurrency={true}
            />
            <StatCard
              title="Total Clientes"
              value={stats.clientes}
              icon={Users}
              color="bg-indigo-600"
              subtext="Registrados"
            />
            <StatCard
              title="Medidores"
              value={stats.medidores}
              icon={TrendingUp}
              color="bg-blue-600"
              subtext="Instalados"
            />
          </>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          Actividad Reciente
        </h3>
        <div className="text-gray-400 text-center py-8">
          {isSuperAdmin
            ? "No hay actividad global reciente."
            : "No hay movimientos recientes en tu empresa."}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardHome;
